/**
 * Contains the RPC object used both by the application
 * site, and by each plugin
 */
import {
  randId,
  typedArrayToDtype,
  dtypeToTypedArray,
  MessageEmitter,
  assert,
  waitFor,
  convertCase,
  expandKwargs,
  Semaphore,
  isAsyncGenerator,
  isGenerator,
  isAsyncIterator,
  isSyncIterator,
} from "./utils/index.js";
import { schemaFunction } from "./utils/schema.js";

import { encode as msgpack_packb, decodeMulti } from "@msgpack/msgpack";
import {
  publicKeyToHex,
  publicKeyFromHex,
  generateEncryptionKeypair,
  encryptPayload,
  decryptPayload,
} from "./crypto.js";

/**
 * Apply an out-of-band encryption public key to all remote methods in a service.
 * @param {Object} svc - The decoded service object.
 * @param {Uint8Array} encPubBytes - 32-byte raw X25519 public key.
 */
function _applyEncryptionKeyToService(svc, encPubBytes) {
  if (svc && typeof svc === "object" && !ArrayBuffer.isView(svc)) {
    for (const key of Object.keys(svc)) {
      const value = svc[key];
      if (typeof value === "function" && value.__rpc_object__) {
        value.__rpc_object__._renc_pub = encPubBytes;
      } else if (
        value &&
        typeof value === "object" &&
        !ArrayBuffer.isView(value)
      ) {
        _applyEncryptionKeyToService(value, encPubBytes);
      }
    }
  }
}

export { _applyEncryptionKeyToService };

export const API_VERSION = 3;
const CHUNK_SIZE = 1024 * 256;
const CONCURRENCY_LIMIT = 30;

const ArrayBufferView = Object.getPrototypeOf(
  Object.getPrototypeOf(new Uint8Array()),
).constructor;

/**
 * Check if a value is a primitive type that needs no encoding/decoding.
 */
function _isPrimitive(v) {
  if (v === null || v === undefined) return true;
  const t = typeof v;
  return t === "number" || t === "string" || t === "boolean";
}

/**
 * Check if an array (and nested arrays/objects) contains only primitives.
 */
function _allPrimitivesArray(arr) {
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i];
    if (_isPrimitive(v)) continue;
    if (v instanceof Uint8Array) continue;
    if (Array.isArray(v)) {
      if (!_allPrimitivesArray(v)) return false;
      continue;
    }
    if (v && v.constructor === Object) {
      if ("_rtype" in v) return false;
      if (!_allPrimitivesObject(v)) return false;
      continue;
    }
    return false;
  }
  return true;
}

/**
 * Check if an object (and nested objects/arrays) contains only primitives.
 */
function _allPrimitivesObject(obj) {
  const values = Object.values(obj);
  for (let i = 0; i < values.length; i++) {
    const v = values[i];
    if (_isPrimitive(v)) continue;
    if (v instanceof Uint8Array) continue;
    if (Array.isArray(v)) {
      if (!_allPrimitivesArray(v)) return false;
      continue;
    }
    if (v && v.constructor === Object) {
      if ("_rtype" in v) return false;
      if (!_allPrimitivesObject(v)) return false;
      continue;
    }
    return false;
  }
  return true;
}

function _appendBuffer(buffer1, buffer2) {
  const tmp = new Uint8Array(buffer1.byteLength + buffer2.byteLength);
  tmp.set(new Uint8Array(buffer1), 0);
  tmp.set(new Uint8Array(buffer2), buffer1.byteLength);
  return tmp.buffer;
}

/**
 * Wrap a promise with a timeout.
 * @param {Promise} promise - The promise to wrap.
 * @param {number} timeoutMs - The timeout in milliseconds.
 * @param {string} message - Optional error message for timeout.
 * @returns {Promise} - The wrapped promise that will reject on timeout.
 */
function withTimeout(promise, timeoutMs, message = "Operation timed out") {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(`TimeoutError: ${message}`));
    }, timeoutMs);

    promise
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });
}

function indexObject(obj, is) {
  if (!is) throw new Error("undefined index");
  if (typeof is === "string") return indexObject(obj, is.split("."));
  else if (is.length === 0) return obj;
  else return indexObject(obj[is[0]], is.slice(1));
}

// Simple fallback schema generation - no docstring parsing for JS

function _get_schema(obj, name = null, skipContext = false) {
  if (Array.isArray(obj)) {
    return obj.map((v, i) => _get_schema(v, null, skipContext));
  } else if (typeof obj === "object" && obj !== null) {
    let schema = {};
    for (let k in obj) {
      schema[k] = _get_schema(obj[k], k, skipContext);
    }
    return schema;
  } else if (typeof obj === "function") {
    if (obj.__schema__) {
      const schema = JSON.parse(JSON.stringify(obj.__schema__));
      if (name) {
        schema.name = name;
        obj.__schema__.name = name;
      }
      if (skipContext) {
        if (schema.parameters && schema.parameters.properties) {
          delete schema.parameters.properties["context"];
        }
        if (schema.parameters && schema.parameters.required) {
          const contextIndex = schema.parameters.required.indexOf("context");
          if (contextIndex > -1) {
            schema.parameters.required.splice(contextIndex, 1);
          }
        }
      }
      return { type: "function", function: schema };
    } else {
      // Simple fallback for JavaScript - just return basic function schema with name
      const funcName = name || obj.name || "function";
      return {
        type: "function",
        function: {
          name: funcName,
        },
      };
    }
  } else if (typeof obj === "number") {
    return { type: "number" };
  } else if (typeof obj === "string") {
    return { type: "string" };
  } else if (typeof obj === "boolean") {
    return { type: "boolean" };
  } else if (obj === null) {
    return { type: "null" };
  } else {
    return {};
  }
}

function _annotate_service(service, serviceTypeInfo) {
  function validateKeys(serviceDict, schemaDict, path = "root") {
    // Validate that all keys in schemaDict exist in serviceDict
    for (let key in schemaDict) {
      if (!serviceDict.hasOwnProperty(key)) {
        throw new Error(`Missing key '${key}' in service at path '${path}'`);
      }
    }

    // Check for any unexpected keys in serviceDict
    for (let key in serviceDict) {
      if (key !== "type" && !schemaDict.hasOwnProperty(key)) {
        throw new Error(`Unexpected key '${key}' in service at path '${path}'`);
      }
    }
  }

  function annotateRecursive(newService, schemaInfo, path = "root") {
    if (typeof newService === "object" && !Array.isArray(newService)) {
      validateKeys(newService, schemaInfo, path);
      for (let k in newService) {
        let v = newService[k];
        let newPath = `${path}.${k}`;
        if (typeof v === "object" && !Array.isArray(v)) {
          annotateRecursive(v, schemaInfo[k], newPath);
        } else if (typeof v === "function") {
          if (schemaInfo.hasOwnProperty(k)) {
            newService[k] = schemaFunction(v, {
              name: schemaInfo[k]["name"],
              description: schemaInfo[k].description || "",
              parameters: schemaInfo[k]["parameters"],
            });
          } else {
            throw new Error(
              `Missing schema for function '${k}' at path '${newPath}'`,
            );
          }
        }
      }
    } else if (Array.isArray(newService)) {
      if (newService.length !== schemaInfo.length) {
        throw new Error(`Length mismatch at path '${path}'`);
      }
      newService.forEach((v, i) => {
        let newPath = `${path}[${i}]`;
        if (typeof v === "object" && !Array.isArray(v)) {
          annotateRecursive(v, schemaInfo[i], newPath);
        } else if (typeof v === "function") {
          if (schemaInfo.hasOwnProperty(i)) {
            newService[i] = schemaFunction(v, {
              name: schemaInfo[i]["name"],
              description: schemaInfo[i].description || "",
              parameters: schemaInfo[i]["parameters"],
            });
          } else {
            throw new Error(
              `Missing schema for function at index ${i} in path '${newPath}'`,
            );
          }
        }
      });
    }
  }

  validateKeys(service, serviceTypeInfo["definition"]);
  annotateRecursive(service, serviceTypeInfo["definition"]);
  return service;
}

function getFunctionInfo(func) {
  const funcString = func.toString();

  // Extract function name
  const nameMatch = funcString.match(/function\s*(\w*)/);
  const name = (nameMatch && nameMatch[1]) || "";

  // Extract function parameters, excluding comments
  const paramsMatch = funcString.match(/\(([^)]*)\)/);
  let params = "";
  if (paramsMatch) {
    params = paramsMatch[1]
      .split(",")
      .map((p) =>
        p
          .replace(/\/\*.*?\*\//g, "") // Remove block comments
          .replace(/\/\/.*$/g, ""),
      ) // Remove line comments
      .filter((p) => p.trim().length > 0) // Remove empty strings after removing comments
      .map((p) => p.trim()) // Trim remaining whitespace
      .join(", ");
  }

  // Extract function docstring (block comment)
  let docMatch = funcString.match(/\)\s*\{\s*\/\*([\s\S]*?)\*\//);
  const docstringBlock = (docMatch && docMatch[1].trim()) || "";

  // Extract function docstring (line comment)
  docMatch = funcString.match(/\)\s*\{\s*(\/\/[\s\S]*?)\n\s*[^\s\/]/);
  const docstringLine =
    (docMatch &&
      docMatch[1]
        .split("\n")
        .map((s) => s.replace(/^\/\/\s*/, "").trim())
        .join("\n")) ||
    "";

  const docstring = docstringBlock || docstringLine;
  return (
    name &&
    params.length > 0 && {
      name: name,
      sig: params,
      doc: docstring,
    }
  );
}

function concatArrayBuffers(buffers) {
  var buffersLengths = buffers.map(function (b) {
      return b.byteLength;
    }),
    totalBufferlength = buffersLengths.reduce(function (p, c) {
      return p + c;
    }, 0),
    unit8Arr = new Uint8Array(totalBufferlength);
  buffersLengths.reduce(function (p, c, i) {
    unit8Arr.set(new Uint8Array(buffers[i]), p);
    return p + c;
  }, 0);
  return unit8Arr.buffer;
}

class Timer {
  constructor(timeout, callback, args, label) {
    this._timeout = timeout;
    this._callback = callback;
    this._args = args;
    this._label = label || "timer";
    this._task = null;
    this.started = false;
  }

  start() {
    if (this.started) {
      this.reset();
    } else {
      this._task = setTimeout(() => {
        this._callback.apply(this, this._args);
      }, this._timeout * 1000);
      this.started = true;
    }
  }

  clear() {
    if (this._task && this.started) {
      clearTimeout(this._task);
      this._task = null;
      this.started = false;
    } else {
      console.warn(`Clearing a timer (${this._label}) which is not started`);
    }
  }

  reset() {
    if (this._task) {
      clearTimeout(this._task);
    }
    this._task = setTimeout(() => {
      this._callback.apply(this, this._args);
    }, this._timeout * 1000);
    this.started = true;
  }
}

class RemoteService extends Object {}

/**
 * RPC object represents a single site in the
 * communication protocol between the application and the plugin
 *
 * @param {Object} connection a special object allowing to send
 * and receive messages from the opposite site (basically it
 * should only provide send() and onMessage() methods)
 */
export class RPC extends MessageEmitter {
  constructor(
    connection,
    {
      client_id = null,
      default_context = null,
      name = null,
      codecs = null,
      method_timeout = null,
      max_message_buffer_size = 0,
      debug = false,
      workspace = null,
      silent = false,
      app_id = null,
      server_base_url = null,
      long_message_chunk_size = null,
      encryption = false,
      encryption_private_key = null,
      encryption_public_key = null,
    },
  ) {
    super(debug);
    this._codecs = codecs || {};
    assert(client_id && typeof client_id === "string");
    assert(client_id, "client_id is required");
    this._client_id = client_id;
    this._name = name;
    this._app_id = app_id || "*";
    this._local_workspace = workspace;
    this._silent = silent;
    this.default_context = default_context || {};
    this._method_annotations = new WeakMap();
    this._max_message_buffer_size = max_message_buffer_size;
    this._chunk_store = {};
    this._method_timeout = method_timeout || 30;
    this._server_base_url = server_base_url;
    this._long_message_chunk_size = long_message_chunk_size || CHUNK_SIZE;

    // make sure there is an execute function
    this._services = {};
    this._object_store = {
      services: this._services,
    };
    // Index: target_id -> Set of top-level session keys for fast cleanup
    this._targetIdIndex = {};
    // Index: allowed_caller -> Set of _rintf service IDs for lifecycle cleanup
    this._rintfCallerIndex = {};
    // Track last known manager_id for stale call rejection on reconnection
    this._last_manager_id = null;

    // Encryption support (X25519 ECDH + AES-256-GCM)
    this._encryption_enabled = false;
    this._encryption_private_key = null;
    this._encryption_public_key = null;
    this._encryption_ready = null;
    if (encryption) {
      if (encryption_private_key && encryption_public_key) {
        this._encryption_private_key = encryption_private_key;
        this._encryption_public_key = encryption_public_key;
        this._encryption_enabled = true;
        this._encryption_ready = Promise.resolve();
      } else {
        this._encryption_ready = generateEncryptionKeypair().then((keyPair) => {
          this._encryption_private_key = keyPair.privateKey;
          this._encryption_public_key = keyPair.publicKey;
          this._encryption_enabled = true;
        });
      }
    }

    // Track background tasks for proper cleanup
    this._background_tasks = new Set();

    // Periodic session sweep for stale sessions with no activity.
    // Default: 10 minutes (matching Python).
    this._sessionMaxAge = 10 * 60 * 1000;
    this._sessionSweepInterval = setInterval(() => {
      this._sweepStaleSessions();
    }, this._sessionMaxAge / 2);

    // Set up global unhandled promise rejection handler for RPC-related errors
    // Use a class-level reference counter so the handler is added once and removed
    // only when the last RPC instance is closed.
    this._unhandledRejectionHandler = (event) => {
      const reason = event.reason;
      if (reason && typeof reason === "object") {
        const reasonStr = reason.toString();
        if (
          reasonStr.includes("Method not found") ||
          reasonStr.includes("Session not found") ||
          reasonStr.includes("Method expired") ||
          reasonStr.includes("Connection is closed") ||
          reasonStr.includes("Client disconnected") ||
          reasonStr.includes("RPC connection closed")
        ) {
          // console.debug(
            // "Ignoring expected disconnection/method error:",
            // reason,
          // );
          event.preventDefault();
          return;
        }
      }
      console.warn("Unhandled RPC promise rejection:", reason);
    };

    this._unhandledRejectionNodeHandler = null;

    if (!RPC._rejectionHandlerCount) {
      RPC._rejectionHandlerCount = 0;
    }
    RPC._rejectionHandlerCount++;

    if (RPC._rejectionHandlerCount === 1) {
      if (typeof window !== "undefined") {
        window.addEventListener(
          "unhandledrejection",
          this._unhandledRejectionHandler,
        );
      } else if (typeof process !== "undefined") {
        this._unhandledRejectionNodeHandler = (reason, promise) => {
          this._unhandledRejectionHandler({
            reason,
            promise,
            preventDefault: () => {},
          });
        };
        process.on("unhandledRejection", this._unhandledRejectionNodeHandler);
      }
    }

    if (connection) {
      this.add_service({
        id: "built-in",
        type: "built-in",
        name: `Built-in services for ${this._local_workspace}/${this._client_id}`,
        config: {
          require_context: true,
          visibility: "public",
          api_version: API_VERSION,
        },
        ping: this._ping.bind(this),
        get_service: this.get_local_service.bind(this),
        message_cache: {
          create: this._create_message.bind(this),
          append: this._append_message.bind(this),
          set: this._set_message.bind(this),
          process: this._process_message.bind(this),
          remove: this._remove_message.bind(this),
        },
      });
      this._boundHandleMethod = this._handle_method.bind(this);
      this._boundHandleError = console.error;
      this.on("method", this._boundHandleMethod);
      this.on("error", this._boundHandleError);
      this.on("peer_not_found", this._handlePeerNotFound.bind(this));

      assert(connection.emit_message && connection.on_message);
      assert(
        connection.manager_id !== undefined,
        "Connection must have manager_id",
      );
      this._emit_message = connection.emit_message.bind(connection);
      connection.on_message(this._on_message.bind(this));
      this._connection = connection;
      const onConnected = async (connectionInfo) => {
        if (!this._silent && this._connection.manager_id) {
          // Immediately reject all pending calls targeting the old manager.
          // After server restart, the manager_id changes. Any in-flight RPC
          // calls to the old manager will never get a response (the server
          // silently drops messages to unknown */{id} targets). Rejecting
          // them here avoids waiting for the full method timeout (~10-30s).
          const currentManagerId = this._connection.manager_id;
          if (
            this._last_manager_id &&
            this._last_manager_id !== currentManagerId
          ) {
            const oldTarget = `*/${this._last_manager_id}`;
            const cleaned = this._cleanupSessionsForClient(oldTarget);
            if (cleaned > 0) {
              console.info(
                `Rejected ${cleaned} stale call(s) to old manager ${this._last_manager_id}`,
              );
            }
          }
          this._last_manager_id = currentManagerId;

          try {
            // Get fresh manager service (one RPC roundtrip, ~50-100ms)
            const manager = await this.get_manager_service({
              timeout: 20,
              case_conversion: "camel",
            });

            // Fire manager_refreshed IMMEDIATELY — before service re-registration.
            // This allows connectToServer's wm proxy to be updated as soon as
            // possible, minimizing the window where stale methods exist.
            this._fire("manager_refreshed", { manager });

            const services = Object.values(this._services);
            const servicesCount = services.length;
            let registeredCount = 0;
            const failedServices = [];

            // Use timeout for service registration to prevent hanging
            // _method_timeout is in seconds, withTimeout expects milliseconds
            const serviceRegistrationTimeout = (this._method_timeout || 30) * 1000;

            for (let service of services) {
              try {
                const serviceInfo = this._extract_service_info(service);
                await withTimeout(
                  manager.registerService(serviceInfo),
                  serviceRegistrationTimeout,
                  `Timeout registering service ${service.id || "unknown"}`,
                );
                registeredCount++;
              } catch (serviceError) {
                failedServices.push(service.id || "unknown");
                if (
                  serviceError.message &&
                  serviceError.message.includes("TimeoutError")
                ) {
                  console.error(
                    `Timeout registering service ${service.id || "unknown"}`,
                  );
                } else {
                  console.error(
                    `Failed to register service ${service.id || "unknown"}: ${serviceError}`,
                  );
                }
              }
            }

            if (registeredCount === servicesCount) {
              console.info(
                `Successfully registered all ${registeredCount} services with the server`,
              );
            } else {
              console.warn(
                `Only registered ${registeredCount} out of ${servicesCount} services with the server. Failed services: ${failedServices.join(", ")}`,
              );
            }

            // Fire event with registration status
            this._fire("services_registered", {
              total: servicesCount,
              registered: registeredCount,
              failed: failedServices,
            });

            // Subscribe to client_disconnected events if the manager supports it
            try {
              if (
                manager.subscribe &&
                typeof manager.subscribe === "function"
              ) {
                // Clean up previous subscription and handler to prevent
                // duplicates on reconnection (listener leak fix)
                if (this._clientDisconnectedSubscription) {
                  try {
                    if (
                      typeof this._clientDisconnectedSubscription.unsubscribe ===
                      "function"
                    ) {
                      this._clientDisconnectedSubscription.unsubscribe();
                    }
                  } catch (e) {
                    // console.debug(`Error unsubscribing old client_disconnected: ${e}`);
                  }
                  this._clientDisconnectedSubscription = null;
                }
                if (this._boundHandleClientDisconnected) {
                  try {
                    this.off(
                      "client_disconnected",
                      this._boundHandleClientDisconnected,
                    );
                  } catch (e) {
                    // Handler may not be in list if previous setup was interrupted
                  }
                  this._boundHandleClientDisconnected = null;
                }

                // console.debug("Subscribing to client_disconnected events");

                // Store handler at instance level so it can be removed later
                this._boundHandleClientDisconnected = async (event) => {
                  // The client ID is in event.data.id based on the event structure
                  const clientId = event.data?.id || event.client;
                  const workspace = event.data?.workspace;
                  if (clientId && workspace) {
                    // Construct the full client path with workspace prefix
                    const fullClientId = `${workspace}/${clientId}`;
                    // console.debug(
                      // `Client ${fullClientId} disconnected, cleaning up sessions`,
                    // );
                    await this._handleClientDisconnected(fullClientId);
                  } else if (clientId) {
                    // console.debug(
                      // `Client ${clientId} disconnected, cleaning up sessions`,
                    // );
                    await this._handleClientDisconnected(clientId);
                  }
                };

                // Subscribe to the event topic first with timeout
                this._clientDisconnectedSubscription = await withTimeout(
                  manager.subscribe(["client_disconnected"]),
                  serviceRegistrationTimeout,
                  "Timeout subscribing to client_disconnected events",
                );

                // Then register the local event handler
                this.on(
                  "client_disconnected",
                  this._boundHandleClientDisconnected,
                );

                // console.debug(
                  // "Successfully subscribed to client_disconnected events",
                // );
              } else {
                // console.debug(
                  // "Manager does not support subscribe method, skipping client_disconnected handling",
                // );
                this._clientDisconnectedSubscription = null;
              }
            } catch (subscribeError) {
              // console.debug(
                // `Failed to subscribe to client_disconnected events: ${subscribeError}`,
              // );
              this._clientDisconnectedSubscription = null;
            }
          } catch (managerError) {
            console.error(
              `Failed to get manager service for registering services: ${managerError}`,
            );
            // Fire event with error status
            this._fire("services_registration_failed", {
              error: managerError.toString(),
              total_services: Object.keys(this._services).length,
            });
          }
        } else {
          // console.debug("Connection established", connectionInfo);
        }
        if (connectionInfo) {
          if (connectionInfo.public_base_url) {
            this._server_base_url = connectionInfo.public_base_url;
          }
          this._fire("connected", connectionInfo);
        }
      };
      connection.on_connected(onConnected);

      // Register disconnect handler to reject all pending RPC calls
      // This ensures no remote function call hangs forever when the connection drops
      if (typeof connection.on_disconnected === "function") {
        connection.on_disconnected((reason) => {
          // If reconnection is enabled, don't reject pending calls immediately.
          // The timeout mechanism will handle them if reconnection fails,
          // allowing calls to succeed after a successful reconnection.
          if (connection._enable_reconnect) {
            console.info(
              `Connection lost (${reason}), reconnection enabled - pending calls will be handled by timeout`,
            );
            return;
          }
          console.warn(
            `Connection lost (${reason}), rejecting all pending RPC calls`,
          );
          this._rejectPendingCalls(
            `Connection lost: ${reason || "unknown reason"}`,
          );
        });
      }

      onConnected();
    } else {
      this._emit_message = function () {
        console.log("No connection to emit message");
      };
    }
  }

  /**
   * Return this client's X25519 public key as a 64-char hex string.
   * Returns null if encryption is not enabled.
   */
  getPublicKey() {
    if (!this._encryption_enabled || !this._encryption_public_key) return null;
    return publicKeyToHex(this._encryption_public_key);
  }

  register_codec(config) {
    if (!config["name"] || (!config["encoder"] && !config["decoder"])) {
      throw new Error(
        "Invalid codec format, please make sure you provide a name, type, encoder and decoder.",
      );
    } else {
      if (config.type) {
        for (let k of Object.keys(this._codecs)) {
          if (this._codecs[k].type === config.type || k === config.name) {
            delete this._codecs[k];
            console.warn("Remove duplicated codec: " + k);
          }
        }
      }
      this._codecs[config["name"]] = config;
    }
  }

  async _ping(msg, context) {
    assert(msg == "ping");
    return "pong";
  }

  async ping(client_id, timeout) {
    let method = this._generate_remote_method({
      _rserver: this._server_base_url,
      _rtarget: client_id,
      _rmethod: "services.built-in.ping",
      _rpromise: true,
      _rdoc: "Ping a remote client",
    });
    assert((await method("ping", timeout)) == "pong");
  }

  _create_message(key, heartbeat, overwrite, context) {
    if (heartbeat) {
      if (!this._object_store[key]) {
        throw new Error(`session does not exist anymore: ${key}`);
      }
      this._object_store[key]["timer"].reset();
    }

    if (!this._object_store["message_cache"]) {
      this._object_store["message_cache"] = {};
    }

    // Evict stale cache entries (older than 5 minutes) and enforce size limit
    const cache = this._object_store["message_cache"];
    const MAX_CACHE_SIZE = 256;
    const MAX_CACHE_AGE = 5 * 60 * 1000;
    const cacheKeys = Object.keys(cache);
    if (cacheKeys.length >= MAX_CACHE_SIZE) {
      const now = Date.now();
      for (const k of cacheKeys) {
        const entry = cache[k];
        if (
          entry &&
          entry._cache_created_at &&
          now - entry._cache_created_at > MAX_CACHE_AGE
        ) {
          delete cache[k];
        }
      }
      // If still over limit, evict oldest entries
      const remaining = Object.keys(cache);
      if (remaining.length >= MAX_CACHE_SIZE) {
        remaining
          .sort(
            (a, b) =>
              (cache[a]._cache_created_at || 0) -
              (cache[b]._cache_created_at || 0),
          )
          .slice(0, remaining.length - MAX_CACHE_SIZE + 1)
          .forEach((k) => delete cache[k]);
      }
    }

    if (!overwrite && cache[key]) {
      throw new Error(
        `Message with the same key (${key}) already exists in the cache store, please use overwrite=true or remove it first.`,
      );
    }
    cache[key] = [];
    cache[key]._cache_created_at = Date.now();
  }

  _append_message(key, data, heartbeat, context) {
    if (heartbeat) {
      if (!this._object_store[key]) {
        throw new Error(`session does not exist anymore: ${key}`);
      }
      this._object_store[key]["timer"].reset();
    }
    const cache = this._object_store["message_cache"];
    if (!cache[key]) {
      throw new Error(`Message with key ${key} does not exists.`);
    }
    assert(data instanceof ArrayBufferView);
    cache[key].push(data);
  }

  _set_message(key, index, data, heartbeat, context) {
    if (heartbeat) {
      if (!this._object_store[key]) {
        throw new Error(`session does not exist anymore: ${key}`);
      }
      this._object_store[key]["timer"].reset();
    }
    const cache = this._object_store["message_cache"];
    if (!cache[key]) {
      throw new Error(`Message with key ${key} does not exists.`);
    }
    assert(data instanceof ArrayBufferView);
    cache[key][index] = data;
  }

  _remove_message(key, context) {
    const cache = this._object_store["message_cache"];
    if (!cache[key]) {
      throw new Error(`Message with key ${key} does not exists.`);
    }
    delete cache[key];
  }

  _process_message(key, heartbeat, context) {
    if (heartbeat) {
      if (!this._object_store[key]) {
        throw new Error(`session does not exist anymore: ${key}`);
      }
      this._object_store[key]["timer"].reset();
    }
    const cache = this._object_store["message_cache"];
    assert(!!context, "Context is required");
    if (!cache[key]) {
      throw new Error(`Message with key ${key} does not exists.`);
    }
    cache[key] = concatArrayBuffers(cache[key]);
    // console.debug(`Processing message ${key} (bytes=${cache[key].byteLength})`);
    let unpacker = decodeMulti(cache[key]);
    const { done, value } = unpacker.next();
    const main = value;
    // Make sure the fields are from trusted source
    Object.assign(main, {
      from: context.from,
      to: context.to,
      ws: context.ws,
      user: context.user,
    });
    main["ctx"] = JSON.parse(JSON.stringify(main));
    Object.assign(main["ctx"], this.default_context);
    if (!done) {
      let extra = unpacker.next();
      Object.assign(main, extra.value);
    }
    this._fire(main["type"], main);
    // console.debug(
    //   this._client_id,
    //   `Processed message ${key} (bytes=${cache[key].byteLength})`,
    // );
    delete cache[key];
  }

  _on_message(message) {
    if (typeof message === "string") {
      const main = JSON.parse(message);
      // Add trusted context to the method call
      main["ctx"] = Object.assign({}, main, this.default_context);
      this._fire(main["type"], main);
    } else if (message instanceof ArrayBuffer || ArrayBuffer.isView(message)) {
      // Handle both ArrayBuffer (WebSocket) and Uint8Array/ArrayBufferView (HTTP transport)
      let unpacker = decodeMulti(message);
      const { done, value } = unpacker.next();
      const main = value;
      // Add trusted context to the method call
      main["ctx"] = Object.assign({}, main, this.default_context);
      if (!done) {
        let extra = unpacker.next();
        Object.assign(main, extra.value);
      }
      this._fire(main["type"], main);
    } else if (typeof message === "object") {
      // Add trusted context to the method call
      message["ctx"] = Object.assign({}, message, this.default_context);
      this._fire(message["type"], message);
    } else {
      throw new Error("Invalid message format");
    }
  }

  reset() {
    this._removeRejectionHandler();
    this._event_handlers = {};
    this._services = {};
  }

  _removeRejectionHandler() {
    if (RPC._rejectionHandlerCount && RPC._rejectionHandlerCount > 0) {
      RPC._rejectionHandlerCount--;
      if (RPC._rejectionHandlerCount === 0) {
        if (typeof window !== "undefined" && this._unhandledRejectionHandler) {
          window.removeEventListener(
            "unhandledrejection",
            this._unhandledRejectionHandler,
          );
        } else if (
          typeof process !== "undefined" &&
          this._unhandledRejectionNodeHandler
        ) {
          process.removeListener(
            "unhandledRejection",
            this._unhandledRejectionNodeHandler,
          );
        }
      }
    }
    this._unhandledRejectionHandler = null;
    this._unhandledRejectionNodeHandler = null;
  }

  close() {
    // Clean up all pending sessions (rejects promises, clears timers/heartbeats, deletes sessions)
    this._cleanupOnDisconnect();

    // Remove method and error event listeners
    if (this._boundHandleMethod) {
      this.off("method", this._boundHandleMethod);
      this._boundHandleMethod = null;
    }
    if (this._boundHandleError) {
      this.off("error", this._boundHandleError);
      this._boundHandleError = null;
    }

    // Clean up client_disconnected subscription and handler
    if (this._clientDisconnectedSubscription) {
      try {
        if (
          typeof this._clientDisconnectedSubscription.unsubscribe === "function"
        ) {
          this._clientDisconnectedSubscription.unsubscribe();
        }
      } catch (e) {
        // console.debug(`Error unsubscribing client_disconnected: ${e}`);
      }
      this._clientDisconnectedSubscription = null;
    }
    if (this._boundHandleClientDisconnected) {
      this.off("client_disconnected", this._boundHandleClientDisconnected);
      this._boundHandleClientDisconnected = null;
    }

    // Remove the global unhandled rejection handler
    this._removeRejectionHandler();

    // Remove ALL remaining event handlers to prevent memory leaks
    // This clears any custom event listeners registered by users via .on()
    this.off();

    // Clean up background tasks
    try {
      for (const task of this._background_tasks) {
        if (task && typeof task.cancel === "function") {
          try {
            task.cancel();
          } catch (e) {
            // console.debug(`Error canceling background task: ${e}`);
          }
        }
      }
      this._background_tasks.clear();
    } catch (e) {
      // console.debug(`Error cleaning up background tasks: ${e}`);
    }

    // Clear session sweep interval
    if (this._sessionSweepInterval) {
      clearInterval(this._sessionSweepInterval);
      this._sessionSweepInterval = null;
    }

    // Clean up connection references to prevent circular references
    try {
      this._connection = null;
      this._emit_message = function () {
        // console.debug("RPC connection closed, ignoring message");
        return Promise.reject(new Error("Connection is closed"));
      };
    } catch (e) {
      // console.debug(`Error during connection cleanup: ${e}`);
    }

    this._fire("disconnected");
  }

  /**
   * Unregister a single _rintf service and remove it from the caller index.
   * Safe to call even if the service was already removed.
   */
  _unregisterRintfService(serviceId, allowedCaller) {
    if (this._services[serviceId]) {
      delete this._services[serviceId];
    }
    if (allowedCaller && this._rintfCallerIndex[allowedCaller]) {
      this._rintfCallerIndex[allowedCaller].delete(serviceId);
      if (this._rintfCallerIndex[allowedCaller].size === 0) {
        delete this._rintfCallerIndex[allowedCaller];
      }
    }
  }

  /**
   * Remove all _rintf services whose allowed caller is the given client.
   * Passive lifecycle cleanup: when a client disconnects, any _rintf
   * callbacks that only it could invoke become dead resources.
   * @returns {number} Number of _rintf services cleaned up
   */
  _cleanupRintfForCaller(clientId) {
    const serviceIds = this._rintfCallerIndex[clientId];
    if (!serviceIds) return 0;
    delete this._rintfCallerIndex[clientId];
    let cleaned = 0;
    for (const sid of serviceIds) {
      if (this._services[sid]) {
        delete this._services[sid];
        cleaned++;
      }
    }
    return cleaned;
  }

  async _handleClientDisconnected(clientId) {
    try {
      // Clean up all sessions for the disconnected client
      const sessionsCleaned = this._cleanupSessionsForClient(clientId);

      // Clean up _rintf services whose allowed caller is the disconnected client
      const rintfCleaned = this._cleanupRintfForCaller(clientId);

      // Fire an event to notify about the client disconnection
      this._fire("remote_client_disconnected", {
        client_id: clientId,
        sessions_cleaned: sessionsCleaned,
        rintf_cleaned: rintfCleaned,
      });
    } catch (e) {
      console.error(
        `Error handling client disconnection for ${clientId}: ${e}`,
      );
    }
  }

  _handlePeerNotFound(data) {
    /**
     * Handle server notification that target peer is not connected.
     *
     * When the server detects that an RPC message targets a disconnected
     * client, it sends back a 'peer_not_found' message instead of silently
     * dropping it. This allows pending calls to fail immediately.
     */
    const sessionId = data.session;
    const peerId = data.peer_id || data.from || "unknown";
    const errorMsg = data.error || `Peer ${peerId} is not connected`;
    // console.debug(`Peer not found: ${peerId} (session=${sessionId})`);

    // Reject the specific pending call identified by sessionId
    if (sessionId) {
      const session = this._object_store[sessionId];
      if (session && typeof session === "object") {
        this._cleanupSessionEntry(session, errorMsg);
        delete this._object_store[sessionId];
        this._removeFromTargetIdIndex(sessionId);
      }
    }

    // Also clean up all other sessions targeting this peer
    if (peerId) {
      this._cleanupSessionsForClient(peerId);
    }
  }

  _removeFromTargetIdIndex(sessionId) {
    /**
     * Remove a session from the target_id index.
     * Call this before removing a session from _object_store.
     */
    const topKey = sessionId.split(".")[0];
    const session = this._object_store[topKey];
    if (session && typeof session === "object") {
      const targetId = session.target_id;
      if (targetId && targetId in this._targetIdIndex) {
        this._targetIdIndex[targetId].delete(topKey);
        if (this._targetIdIndex[targetId].size === 0) {
          delete this._targetIdIndex[targetId];
        }
      }
    }
  }

  /**
   * Clean up a single session entry: reject promise, clear timer, cancel heartbeat.
   * Centralizes the cleanup logic used by multiple methods.
   * @param {object} session - The session object from _object_store
   * @param {string|null} rejectReason - If provided, reject the session's promise with this reason
   */
  _cleanupSessionEntry(session, rejectReason = null) {
    if (!session || typeof session !== "object") return;
    if (
      rejectReason &&
      session.reject &&
      typeof session.reject === "function"
    ) {
      try {
        session.reject(new Error(rejectReason));
      } catch (e) {
        // console.debug(`Error rejecting session: ${e}`);
      }
    }
    if (session.heartbeat_task) {
      try {
        clearInterval(session.heartbeat_task);
      } catch (e) {
        /* ignore */
      }
    }
    if (
      session.timer &&
      session.timer.started &&
      typeof session.timer.clear === "function"
    ) {
      try {
        session.timer.clear();
      } catch (e) {
        /* ignore */
      }
    }
  }

  _cleanupSessionsForClient(clientId) {
    let sessionsCleaned = 0;

    // Use index for O(1) lookup instead of iterating all sessions
    const sessionKeys = this._targetIdIndex[clientId];
    if (!sessionKeys) return 0;

    const reason = `Client disconnected: ${clientId}`;
    for (const sessionKey of sessionKeys) {
      const session = this._object_store[sessionKey];
      if (!session || typeof session !== "object") continue;
      if (session.target_id !== clientId) continue;

      this._cleanupSessionEntry(session, reason);
      delete this._object_store[sessionKey];
      sessionsCleaned++;
      // console.debug(`Cleaned up session: ${sessionKey}`);
    }

    delete this._targetIdIndex[clientId];
    return sessionsCleaned;
  }

  _rejectPendingCalls(reason = "Connection lost") {
    /**
     * Reject all pending RPC calls when the connection is lost.
     * Does NOT remove sessions (connection might be re-established).
     */
    try {
      let rejectedCount = 0;
      for (const key of Object.keys(this._object_store)) {
        if (key === "services" || key === "message_cache") continue;
        const value = this._object_store[key];
        if (typeof value === "object" && value !== null) {
          if (value.reject && typeof value.reject === "function") {
            rejectedCount++;
          }
          this._cleanupSessionEntry(value, reason);
        }
      }
      if (rejectedCount > 0) {
        console.warn(
          `Rejected ${rejectedCount} pending RPC call(s) due to: ${reason}`,
        );
      }
    } catch (e) {
      console.error(`Error rejecting pending calls: ${e}`);
    }
  }

  _cleanupOnDisconnect() {
    try {
      // console.debug("Cleaning up all sessions due to local RPC disconnection");

      const keysToDelete = [];
      for (const key of Object.keys(this._object_store)) {
        if (key === "services" || key === "message_cache") continue;
        const value = this._object_store[key];
        this._cleanupSessionEntry(value, "RPC connection closed");
        keysToDelete.push(key);
      }

      for (const key of keysToDelete) {
        delete this._object_store[key];
      }

      this._targetIdIndex = {};
      this._rintfCallerIndex = {};
    } catch (e) {
      console.error(`Error during cleanup on disconnect: ${e}`);
    }
  }

  async disconnect() {
    // Store connection reference before closing
    const connection = this._connection;
    this.close();

    // Disconnect the underlying connection if it exists
    if (connection) {
      try {
        await connection.disconnect();
      } catch (e) {
        // console.debug(`Error disconnecting underlying connection: ${e}`);
      }
    }
  }

  async get_manager_service(config, maxRetries = 20) {
    config = config || {};
    const baseDelay = 500;
    const maxDelay = 10000;
    let lastError = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const retryDelay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);

      if (!this._connection.manager_id) {
        if (attempt < maxRetries - 1) {
          console.warn(
            `Manager ID not set, retrying in ${retryDelay}ms (attempt ${attempt + 1}/${maxRetries})`,
          );
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          continue;
        } else {
          throw new Error("Manager ID not set after maximum retries");
        }
      }

      try {
        const svc = await this.get_remote_service(
          `*/${this._connection.manager_id}:default`,
          config,
        );
        return svc;
      } catch (e) {
        lastError = e;
        console.warn(
          `Failed to get manager service (attempt ${attempt + 1}/${maxRetries}): ${e.message}`,
        );
        if (attempt < maxRetries - 1) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }
    }

    throw lastError;
  }

  get_all_local_services() {
    return this._services;
  }
  get_local_service(service_id, context) {
    assert(service_id);
    assert(context, "Context is required");

    const [ws, client_id] = context["to"].split("/");
    assert(
      client_id === this._client_id,
      "Services can only be accessed locally",
    );

    const service = this._services[service_id];
    if (!service) {
      throw new Error("Service not found: " + service_id);
    }

    // Note: Do NOT mutate service.config.workspace here!
    // Doing so would corrupt the stored service config when called from
    // a different workspace (e.g., "public"), causing reconnection to fail
    // because _extract_service_info would use the wrong workspace value.

    // allow access for the same workspace
    if (
      service.config.visibility == "public" ||
      service.config.visibility == "unlisted"
    ) {
      return service;
    }

    // allow access for the same workspace
    if (context["ws"] === ws) {
      return service;
    }

    // Check if user is from an authorized workspace
    const authorized_workspaces = service.config.authorized_workspaces;
    if (
      authorized_workspaces &&
      authorized_workspaces.includes(context["ws"])
    ) {
      return service;
    }

    throw new Error(
      `Permission denied for getting protected service: ${service_id}, workspace mismatch: ${ws} != ${context["ws"]}`,
    );
  }
  async get_remote_service(service_uri, config) {
    let { timeout, case_conversion, kwargs_expansion, encryption_public_key } =
      config || {};
    timeout = timeout === undefined ? this._method_timeout : timeout;
    if (!service_uri && this._connection.manager_id) {
      service_uri = "*/" + this._connection.manager_id;
    } else if (!service_uri.includes(":")) {
      service_uri = this._client_id + ":" + service_uri;
    }
    const provider = service_uri.split(":")[0];
    let service_id = service_uri.split(":")[1];
    if (service_id.includes("@")) {
      service_id = service_id.split("@")[0];
      const app_id = service_uri.split("@")[1];
      if (this._app_id && this._app_id !== "*")
        assert(
          app_id === this._app_id,
          `Invalid app id: ${app_id} != ${this._app_id}`,
        );
    }
    assert(provider, `Invalid service uri: ${service_uri}`);

    try {
      const method = this._generate_remote_method({
        _rserver: this._server_base_url,
        _rtarget: provider,
        _rmethod: "services.built-in.get_service",
        _rpromise: true,
        _rdoc: "Get a remote service",
      });
      let svc = await waitFor(
        method(service_id),
        timeout,
        "Timeout Error: Failed to get remote service: " + service_uri,
      );
      svc.id = `${provider}:${service_id}`;
      if (encryption_public_key) {
        const encPubBytes = publicKeyFromHex(encryption_public_key);
        _applyEncryptionKeyToService(svc, encPubBytes);
      }
      if (kwargs_expansion) {
        svc = expandKwargs(svc);
      }
      if (case_conversion)
        return Object.assign(
          new RemoteService(),
          convertCase(svc, case_conversion),
        );
      else return Object.assign(new RemoteService(), svc);
    } catch (e) {
      console.warn("Failed to get remote service: " + service_uri, e);
      throw e;
    }
  }
  _annotate_service_methods(
    aObject,
    object_id,
    require_context,
    run_in_executor,
    visibility,
    authorized_workspaces,
    trusted_keys,
    rintf_allowed_caller,
  ) {
    if (typeof aObject === "function") {
      // mark the method as a remote method that requires context
      let method_name = object_id.split(".")[1];
      this._method_annotations.set(aObject, {
        require_context: Array.isArray(require_context)
          ? require_context.includes(method_name)
          : !!require_context,
        run_in_executor: run_in_executor,
        method_id: "services." + object_id,
        visibility: visibility,
        authorized_workspaces: authorized_workspaces,
        trusted_keys: trusted_keys,
        rintf_allowed_caller: rintf_allowed_caller,
      });
    } else if (aObject instanceof Array || aObject instanceof Object) {
      for (let key of Object.keys(aObject)) {
        let val = aObject[key];
        if (typeof val === "function" && val.__rpc_object__) {
          let client_id = val.__rpc_object__._rtarget;
          if (client_id.includes("/")) {
            client_id = client_id.split("/")[1];
          }
          if (this._client_id === client_id) {
            if (aObject instanceof Array) {
              aObject = aObject.slice();
            }
            // recover local method
            aObject[key] = indexObject(
              this._object_store,
              val.__rpc_object__._rmethod,
            );
            val = aObject[key]; // make sure it's annotated later
          } else {
            throw new Error(
              `Local method not found: ${val.__rpc_object__._rmethod}, client id mismatch ${this._client_id} != ${client_id}`,
            );
          }
        }
        this._annotate_service_methods(
          val,
          object_id + "." + key,
          require_context,
          run_in_executor,
          visibility,
          authorized_workspaces,
          trusted_keys,
          rintf_allowed_caller,
        );
      }
    }
  }
  add_service(api, overwrite) {
    if (!api || Array.isArray(api)) throw new Error("Invalid service object");
    if (api.constructor === Object) {
      api = Object.assign({}, api);
    } else {
      const normApi = {};
      const props = Object.getOwnPropertyNames(api).concat(
        Object.getOwnPropertyNames(Object.getPrototypeOf(api)),
      );
      for (let k of props) {
        if (k !== "constructor") {
          if (typeof api[k] === "function") normApi[k] = api[k].bind(api);
          else normApi[k] = api[k];
        }
      }
      // For class instance, we need set a default id
      api.id = api.id || "default";
      api = normApi;
    }
    assert(
      api.id && typeof api.id === "string",
      `Service id not found: ${api}`,
    );
    if (!api.name) {
      api.name = api.id;
    }
    if (!api.config) {
      api.config = {};
    }
    if (!api.type) {
      api.type = "generic";
    }
    // require_context only applies to the top-level functions
    let require_context = false,
      run_in_executor = false;
    if (api.config.require_context)
      require_context = api.config.require_context;
    if (api.config.run_in_executor) run_in_executor = true;
    const visibility = api.config.visibility || "protected";
    assert(["protected", "public", "unlisted"].includes(visibility));

    // Validate authorized_workspaces
    const authorized_workspaces = api.config.authorized_workspaces;
    if (authorized_workspaces !== undefined) {
      if (visibility !== "protected") {
        throw new Error(
          `authorized_workspaces can only be set when visibility is 'protected', got visibility='${visibility}'`,
        );
      }
      if (!Array.isArray(authorized_workspaces)) {
        throw new Error(
          "authorized_workspaces must be an array of workspace ids",
        );
      }
      for (const ws_id of authorized_workspaces) {
        if (typeof ws_id !== "string") {
          throw new Error(
            `Each workspace id in authorized_workspaces must be a string, got ${typeof ws_id}`,
          );
        }
      }
    }
    const trustedKeysHex = api.config.trusted_keys;
    let trusted_keys = null;
    if (trustedKeysHex && trustedKeysHex.length > 0) {
      if (!Array.isArray(trustedKeysHex)) {
        throw new Error(
          "trusted_keys must be an array of hex-encoded public keys",
        );
      }
      trusted_keys = new Set();
      for (const keyHex of trustedKeysHex) {
        if (typeof keyHex !== "string" || keyHex.length !== 64) {
          throw new Error(
            `Each trusted key must be a 64-char hex string, got: ${keyHex}`,
          );
        }
        trusted_keys.add(keyHex);
      }
    }
    const rintf_allowed_caller = api.config._rintf_allowed_caller || null;
    this._annotate_service_methods(
      api,
      api["id"],
      require_context,
      run_in_executor,
      visibility,
      authorized_workspaces,
      trusted_keys,
      rintf_allowed_caller,
    );

    if (this._services[api.id]) {
      if (overwrite) {
        delete this._services[api.id];
      } else {
        throw new Error(
          `Service already exists: ${api.id}, please specify a different id (not ${api.id}) or overwrite=true`,
        );
      }
    }
    this._services[api.id] = api;
    return api;
  }

  _extract_service_info(service) {
    const config = service.config || {};
    config.workspace =
      config.workspace || this._local_workspace || this._connection.workspace;
    if (!config.workspace) {
      throw new Error(
        "Workspace is not set. Please ensure the connection has a workspace or set local_workspace.",
      );
    }
    const skipContext = config.require_context;
    const excludeKeys = [
      "id",
      "config",
      "name",
      "description",
      "type",
      "docs",
      "app_id",
      "service_schema",
    ];
    const filteredService = {};
    for (const key of Object.keys(service)) {
      if (!excludeKeys.includes(key)) {
        filteredService[key] = service[key];
      }
    }
    const serviceSchema = _get_schema(filteredService, null, skipContext);
    const serviceInfo = {
      config: config,
      id: `${config.workspace}/${this._client_id}:${service["id"]}`,
      name: service.name || service["id"],
      description: service.description || "",
      type: service.type || "generic",
      docs: service.docs || null,
      app_id: this._app_id,
      service_schema: serviceSchema,
    };
    return serviceInfo;
  }

  async get_service_schema(service) {
    const skipContext = service.config.require_context;
    return _get_schema(service, null, skipContext);
  }

  async register_service(api, config) {
    let { check_type, notify, overwrite } = config || {};
    notify = notify === undefined ? true : notify;
    let manager;
    if (check_type && api.type) {
      try {
        manager = await this.get_manager_service({
          timeout: 10,
          case_conversion: "camel",
        });
        const type_info = await manager.get_service_type(api.type);
        api = _annotate_service(api, type_info);
      } catch (e) {
        throw new Error(`Failed to get service type ${api.type}, error: ${e}`);
      }
    }

    const service = this.add_service(api, overwrite);
    const serviceInfo = this._extract_service_info(service);
    if (notify) {
      try {
        manager =
          manager ||
          (await this.get_manager_service({
            timeout: 10,
            case_conversion: "camel",
          }));
        await manager.registerService(serviceInfo);
      } catch (e) {
        throw new Error(`Failed to notify workspace manager: ${e}`);
      }
    }
    return serviceInfo;
  }

  async unregister_service(service, notify) {
    notify = notify === undefined ? true : notify;
    let service_id;
    if (typeof service === "string") {
      service_id = service;
    } else {
      service_id = service.id;
    }
    assert(
      service_id && typeof service_id === "string",
      `Invalid service id: ${service_id}`,
    );
    if (service_id.includes(":")) {
      service_id = service_id.split(":")[1];
    }
    if (service_id.includes("@")) {
      service_id = service_id.split("@")[0];
    }
    if (!this._services[service_id]) {
      throw new Error(`Service not found: ${service_id}`);
    }
    // Auto-detect _rintf services (local-only, never registered with server)
    if (service_id.startsWith("_rintf_")) {
      notify = false;
      // Also clean up from the caller index
      for (const [caller, sids] of Object.entries(this._rintfCallerIndex)) {
        sids.delete(service_id);
        if (sids.size === 0) {
          delete this._rintfCallerIndex[caller];
        }
      }
    }
    if (notify) {
      const manager = await this.get_manager_service({
        timeout: 10,
        case_conversion: "camel",
      });
      await manager.unregisterService(service_id);
    }
    delete this._services[service_id];
  }

  _ndarray(typedArray, shape, dtype) {
    const _dtype = typedArrayToDtype(typedArray);
    if (dtype && dtype !== _dtype) {
      throw (
        "dtype doesn't match the type of the array: " + _dtype + " != " + dtype
      );
    }
    shape = shape || [typedArray.length];
    return {
      _rtype: "ndarray",
      _rvalue: typedArray.buffer,
      _rshape: shape,
      _rdtype: _dtype,
    };
  }

  _encode_callback(
    name,
    callback,
    session_id,
    clear_after_called,
    timer,
    local_workspace,
    description,
  ) {
    let method_id = `${session_id}.${name}`;
    let encoded = {
      _rtype: "method",
      _rtarget: local_workspace
        ? `${local_workspace}/${this._client_id}`
        : this._client_id,
      _rmethod: method_id,
      _rpromise: false,
    };

    const self = this;
    let wrapped_callback = function () {
      try {
        callback.apply(null, Array.prototype.slice.call(arguments));
      } catch (error) {
        console.error(
          `Error in callback(${method_id}, ${description}): ${error}`,
        );
      } finally {
        // Clear the timer first if it exists
        if (timer && timer.started) {
          timer.clear();
        }

        // Clean up the entire session when resolve/reject is called
        if (clear_after_called && self._object_store[session_id]) {
          if (name === "resolve" || name === "reject") {
            self._removeFromTargetIdIndex(session_id);
            delete self._object_store[session_id];
          } else {
            self._cleanup_session_if_needed(session_id, name);
          }
        }
      }
    };
    wrapped_callback.__name__ = `callback(${method_id})`;
    return [encoded, wrapped_callback];
  }

  _cleanup_session_if_needed(session_id, callback_name) {
    /**
     * Clean session management - all logic in one place.
     */
    if (!session_id) {
      // console.debug("Cannot cleanup session: session_id is empty");
      return;
    }

    try {
      const store = this._get_session_store(session_id, false);
      if (!store) {
        // console.debug(`Session ${session_id} not found for cleanup`);
        return;
      }

      let should_cleanup = false;

      // Promise sessions: let the promise manager decide cleanup
      if (store._promise_manager) {
        try {
          const promise_manager = store._promise_manager;
          if (
            promise_manager.should_cleanup_on_callback &&
            promise_manager.should_cleanup_on_callback(callback_name)
          ) {
            if (promise_manager.settle) {
              promise_manager.settle();
            }
            should_cleanup = true;
            // console.debug(
              // `Promise session ${session_id} settled and marked for cleanup`,
            // );
          }
        } catch (e) {
          console.warn(
            `Error in promise manager cleanup for session ${session_id}:`,
            e,
          );
        }
      } else {
        // Regular sessions: only cleanup temporary method call sessions
        // Don't cleanup service registration sessions or persistent sessions
        // Only cleanup sessions that are clearly temporary promises for method calls
        if (
          (callback_name === "resolve" || callback_name === "reject") &&
          store._callbacks &&
          Object.keys(store._callbacks).includes(callback_name)
        ) {
          should_cleanup = true;
          // console.debug(
            // `Regular session ${session_id} marked for cleanup after ${callback_name}`,
          // );
        }
      }

      if (should_cleanup) {
        this._cleanup_session_completely(session_id);
      }
    } catch (error) {
      console.warn(`Error during session cleanup for ${session_id}:`, error);
    }
  }

  _cleanup_session_completely(session_id) {
    /**
     * Complete session cleanup with resource management.
     */
    try {
      // Clean up target_id index before deleting the session
      this._removeFromTargetIdIndex(session_id);

      const store = this._get_session_store(session_id, false);
      if (!store) {
        // console.debug(`Session ${session_id} already cleaned up`);
        return;
      }

      // Clean up resources before removing session
      if (
        store.timer &&
        store.timer.started &&
        typeof store.timer.clear === "function"
      ) {
        try {
          store.timer.clear();
        } catch (error) {
          console.warn(
            `Error clearing timer for session ${session_id}:`,
            error,
          );
        }
      }

      if (
        store.heartbeat_task &&
        typeof store.heartbeat_task.cancel === "function"
      ) {
        try {
          store.heartbeat_task.cancel();
        } catch (error) {
          console.warn(
            `Error canceling heartbeat for session ${session_id}:`,
            error,
          );
        }
      }

      // Navigate and clean session path
      const levels = session_id.split(".");
      let current_store = this._object_store;

      // Navigate to parent of target level
      for (let i = 0; i < levels.length - 1; i++) {
        const level = levels[i];
        if (!current_store[level]) {
          // console.debug(
            // `Session path ${session_id} not found at level ${level}`,
          // );
          return;
        }
        current_store = current_store[level];
      }

      // Delete the final level
      const final_key = levels[levels.length - 1];
      if (current_store[final_key]) {
        delete current_store[final_key];
        // console.debug(`Cleaned up session ${session_id}`);

        // Clean up empty parent containers
        this._cleanup_empty_containers(levels.slice(0, -1));
      }
    } catch (error) {
      console.warn(
        `Error in complete session cleanup for ${session_id}:`,
        error,
      );
    }
  }

  _cleanup_empty_containers(path_levels) {
    /**
     * Clean up empty parent containers to prevent memory leaks.
     */
    try {
      // Work backwards from the deepest level
      for (let depth = path_levels.length - 1; depth >= 0; depth--) {
        let current_store = this._object_store;

        // Navigate to parent of current depth
        for (let i = 0; i < depth; i++) {
          current_store = current_store[path_levels[i]];
          if (!current_store) return; // Path doesn't exist
        }

        // Check if container at current depth is empty
        const container_key = path_levels[depth];
        const container = current_store[container_key];

        if (
          container &&
          typeof container === "object" &&
          Object.keys(container).length === 0
        ) {
          delete current_store[container_key];
          // console.debug(
            // `Cleaned up empty container at depth ${depth}: ${path_levels.slice(0, depth + 1).join(".")}`,
          // );
        } else {
          // Container is not empty, stop cleanup
          break;
        }
      }
    } catch (error) {
      console.warn("Error cleaning up empty containers:", error);
    }
  }

  get_session_stats() {
    /**
     * Get detailed session statistics.
     */
    const stats = {
      total_sessions: 0,
      promise_sessions: 0,
      regular_sessions: 0,
      sessions_with_timers: 0,
      sessions_with_heartbeat: 0,
      system_stores: {},
      session_ids: [],
      memory_usage: 0,
    };

    if (!this._object_store) {
      return stats;
    }

    for (const key in this._object_store) {
      const value = this._object_store[key];

      if (["services", "message_cache"].includes(key)) {
        // System stores - don't count these as sessions
        stats.system_stores[key] = {
          size:
            typeof value === "object" && value ? Object.keys(value).length : 0,
        };
        continue;
      }

      // Count all non-system non-empty objects as sessions
      if (value && typeof value === "object") {
        const sessionKeys = Object.keys(value);

        // Only skip completely empty objects
        if (sessionKeys.length > 0) {
          stats.total_sessions++;
          stats.session_ids.push(key);

          if (value._promise_manager) {
            stats.promise_sessions++;
          } else {
            stats.regular_sessions++;
          }

          if (value._timer || value.timer) stats.sessions_with_timers++;
          if (value._heartbeat || value.heartbeat)
            stats.sessions_with_heartbeat++;

          // Estimate memory usage
          stats.memory_usage += JSON.stringify(value).length;
        }
      }
    }

    return stats;
  }

  _force_cleanup_all_sessions() {
    /**
     * Force cleanup all sessions (for testing purposes).
     */
    if (!this._object_store) {
      // console.debug("Force cleaning up 0 sessions");
      return;
    }

    let cleaned_count = 0;
    const keys_to_delete = [];

    for (const key in this._object_store) {
      // Don't delete system stores
      if (!["services", "message_cache"].includes(key)) {
        const value = this._object_store[key];
        if (
          value &&
          typeof value === "object" &&
          Object.keys(value).length > 0
        ) {
          keys_to_delete.push(key);
          cleaned_count++;
        }
      }
    }

    // Delete the sessions
    for (const key of keys_to_delete) {
      delete this._object_store[key];
    }

    // Clear the target_id index since all sessions are removed
    this._targetIdIndex = {};
    this._rintfCallerIndex = {};

    // console.debug(`Force cleaning up ${cleaned_count} sessions`);
  }

  _sweepStaleSessions() {
    const now = Date.now();
    let swept = 0;
    for (const key of Object.keys(this._object_store)) {
      if (key === "services" || key === "message_cache") continue;
      const session = this._object_store[key];
      // Use last-activity time if available, fall back to creation time
      const lastActivity =
        session && (session._last_activity_at || session._created_at);
      if (
        session &&
        typeof session === "object" &&
        lastActivity &&
        now - lastActivity > this._sessionMaxAge
      ) {
        // Only sweep sessions that have no timer (active timers mean they are in use)
        // and no active promise callbacks (resolve/reject mean the session is awaiting a response)
        if (!session.timer || !session.timer.started) {
          if (
            typeof session.resolve === "function" ||
            typeof session.reject === "function"
          ) {
            // Session still has active promise callbacks, skip it
            continue;
          }
          this._removeFromTargetIdIndex(key);
          if (session.heartbeat_task) clearInterval(session.heartbeat_task);
          delete this._object_store[key];
          swept++;
        }
      }
    }
    if (swept > 0) {
      // console.debug(`Swept ${swept} stale session(s)`);
    }
  }

  // Clean helper to identify promise method calls by session type
  _is_promise_method_call(method_path) {
    const session_id = method_path.split(".")[0];
    const session = this._get_session_store(session_id, false);
    return session && session._promise_manager;
  }

  // Simplified Promise Manager - enhanced version
  _create_promise_manager() {
    /**
     * Create a promise manager to track promise state and decide cleanup.
     */
    return {
      should_cleanup_on_callback: (callback_name) => {
        return ["resolve", "reject"].includes(callback_name);
      },
      settle: () => {
        // Promise is settled (resolved or rejected)
        // console.debug("Promise settled");
      },
    };
  }

  async _encode_promise(
    resolve,
    reject,
    session_id,
    clear_after_called,
    timer,
    local_workspace,
    description,
  ) {
    let store = this._get_session_store(session_id, true);
    if (!store) {
      console.warn(
        `Failed to create session store ${session_id}, session management may be impaired`,
      );
      store = {};
    }

    // Clean promise lifecycle management - TYPE-BASED, not string-based
    store._promise_manager = this._create_promise_manager();

    let encoded = {};

    if (timer && reject && this._method_timeout) {
      [encoded.heartbeat, store.heartbeat] = this._encode_callback(
        "heartbeat",
        timer.reset.bind(timer),
        session_id,
        false,
        null,
        local_workspace,
      );
      store.timer = timer;
      encoded.interval = this._method_timeout / 2;
    } else {
      timer = null;
    }

    [encoded.resolve, store.resolve] = this._encode_callback(
      "resolve",
      resolve,
      session_id,
      clear_after_called,
      timer,
      local_workspace,
      `resolve (${description})`,
    );
    [encoded.reject, store.reject] = this._encode_callback(
      "reject",
      reject,
      session_id,
      clear_after_called,
      timer,
      local_workspace,
      `reject (${description})`,
    );
    return encoded;
  }

  async _send_chunks(data, target_id, session_id) {
    // 1) Get the remote service
    const remote_services = await this.get_remote_service(
      `${target_id}:built-in`,
    );
    if (!remote_services.message_cache) {
      throw new Error(
        "Remote client does not support message caching for large messages.",
      );
    }

    const message_cache = remote_services.message_cache;
    const message_id = session_id || randId();
    const total_size = data.length;
    const start_time = Date.now(); // measure time
    const chunk_num = Math.ceil(total_size / this._long_message_chunk_size);
    if (remote_services.config.api_version >= 3) {
      await message_cache.create(message_id, !!session_id);
      const semaphore = new Semaphore(CONCURRENCY_LIMIT);

      const tasks = [];
      for (let idx = 0; idx < chunk_num; idx++) {
        const startByte = idx * this._long_message_chunk_size;
        const chunk = data.slice(
          startByte,
          startByte + this._long_message_chunk_size,
        );

        const taskFn = async () => {
          await message_cache.set(message_id, idx, chunk, !!session_id);
          // console.debug(
          //   `Sending chunk ${idx + 1}/${chunk_num} (total=${total_size} bytes)`,
          // );
        };

        // Push into an array, each one runs under the semaphore
        tasks.push(semaphore.run(taskFn));
      }

      // Wait for all chunk uploads to finish
      try {
        await Promise.all(tasks);
      } catch (error) {
        // If any chunk fails, clean up the message cache
        try {
          await message_cache.remove(message_id);
        } catch (cleanupError) {
          console.error(
            `Failed to clean up message cache after error: ${cleanupError}`,
          );
        }
        throw error;
      }
    } else {
      // 3) Legacy version (sequential appends):
      await message_cache.create(message_id, !!session_id);
      for (let idx = 0; idx < chunk_num; idx++) {
        const startByte = idx * this._long_message_chunk_size;
        const chunk = data.slice(
          startByte,
          startByte + this._long_message_chunk_size,
        );
        await message_cache.append(message_id, chunk, !!session_id);
        // console.debug(
        //   `Sending chunk ${idx + 1}/${chunk_num} (total=${total_size} bytes)`,
        // );
      }
    }
    await message_cache.process(message_id, !!session_id);
    const durationSec = ((Date.now() - start_time) / 1000).toFixed(2);
    // console.debug(`All chunks (${total_size} bytes) sent in ${durationSec} s`);
  }

  emit(main_message, extra_data) {
    assert(
      typeof main_message === "object" && main_message.type,
      "Invalid message, must be an object with a `type` fields.",
    );
    if (!main_message.to) {
      this._fire(main_message.type, main_message);
      return;
    }

    let message_package = msgpack_packb(main_message);
    if (extra_data) {
      const extra = msgpack_packb(extra_data);
      const combined = new Uint8Array(message_package.length + extra.length);
      combined.set(message_package);
      combined.set(extra, message_package.length);
      message_package = combined;
    }
    const total_size = message_package.length;
    if (total_size > this._long_message_chunk_size + 1024) {
      console.warn(`Sending large message (size=${total_size})`);
    }
    return this._emit_message(message_package);
  }

  _generate_remote_method(
    encoded_method,
    remote_parent,
    local_parent,
    remote_workspace,
    local_workspace,
  ) {
    let target_id = encoded_method._rtarget;
    if (remote_workspace && !target_id.includes("/")) {
      // Don't modify target_id if it starts with */ (workspace manager service)
      if (!target_id.startsWith("*/")) {
        if (remote_workspace !== target_id) {
          target_id = remote_workspace + "/" + target_id;
        }
        // Fix the target id to be an absolute id
        encoded_method._rtarget = target_id;
      }
    }
    let method_id = encoded_method._rmethod;
    let with_promise = encoded_method._rpromise || false;
    const description = `method: ${method_id}, docs: ${encoded_method._rdoc}`;
    const self = this;

    function remote_method() {
      return new Promise(async (resolve, reject) => {
        try {
          // Read target_id from encoded_method at call time (not captured
          // at generation time) so that _rtarget can be updated after
          // reconnection without regenerating the method closures.
          const target_id = encoded_method._rtarget;
          let local_session_id = randId();
          if (local_parent) {
            // Store the children session under the parent
            local_session_id = local_parent + "." + local_session_id;
          }
          let store = self._get_session_store(local_session_id, true);
          if (!store) {
            reject(
              new Error(
                `Runtime Error: Failed to get session store ${local_session_id} (context: ${description})`,
              ),
            );
            return;
          }
          store["target_id"] = target_id;
          // Update target_id index for fast session cleanup
          const topKey = local_session_id.split(".")[0];
          if (!(target_id in self._targetIdIndex)) {
            self._targetIdIndex[target_id] = new Set();
          }
          self._targetIdIndex[target_id].add(topKey);
          const args = await self._encode(
            Array.prototype.slice.call(arguments),
            local_session_id,
            local_workspace,
          );
          const argLength = args.length;
          // if the last argument is an object, mark it as kwargs
          const withKwargs =
            argLength > 0 &&
            typeof args[argLength - 1] === "object" &&
            args[argLength - 1] !== null &&
            args[argLength - 1]._rkwargs;
          if (withKwargs) delete args[argLength - 1]._rkwargs;

          let from_client;
          if (!self._local_workspace) {
            from_client = self._client_id;
          } else {
            from_client = self._local_workspace + "/" + self._client_id;
          }

          let main_message = {
            type: "method",
            from: from_client,
            to: target_id,
            method: method_id,
          };
          let extra_data = {};
          if (args) {
            extra_data["args"] = args;
          }
          if (withKwargs) {
            extra_data["with_kwargs"] = withKwargs;
          }

          // console.log(
          //   `Calling remote method ${target_id}:${method_id}, session: ${local_session_id}`
          // );
          if (remote_parent) {
            // Set the parent session
            // Note: It's a session id for the remote, not the current client
            main_message["parent"] = remote_parent;
          }

          let timer = null;
          if (with_promise) {
            // Only pass the current session id to the remote
            // if we want to received the result
            // I.e. the session id won't be passed for promises themselves
            main_message["session"] = local_session_id;
            let method_name = `${target_id}:${method_id}`;

            // Create a timer that gets reset by heartbeat
            // Methods can run indefinitely as long as heartbeat keeps resetting the timer
            // IMPORTANT: When timeout occurs, we must clean up the session to prevent memory leaks
            const timeoutCallback = function (error_msg) {
              // First reject the promise - wrap in Error for proper stack traces
              reject(new Error(error_msg));
              // Then clean up the entire session to stop all callbacks
              if (self._object_store[local_session_id]) {
                // Clean up target_id index before deleting the session
                self._removeFromTargetIdIndex(local_session_id);
                delete self._object_store[local_session_id];
                // console.debug(
                  // `Cleaned up session ${local_session_id} after timeout`,
                // );
              }
            };

            timer = new Timer(
              self._method_timeout,
              timeoutCallback,
              [
                `Method call timed out: ${method_name}, context: ${description}`,
              ],
              method_name,
            );
            let clear_after_called = true;

            const promiseData = await self._encode_promise(
              resolve,
              reject,
              local_session_id,
              clear_after_called,
              timer,
              local_workspace,
              description,
            );

            if (with_promise === true) {
              extra_data["promise"] = promiseData;
            } else if (with_promise === "*") {
              extra_data["promise"] = "*";
              extra_data["t"] = self._method_timeout / 2;
            } else {
              throw new Error(`Unsupported promise type: ${with_promise}`);
            }
          }
          // E2E encrypt extra_data if both sides support encryption
          if (self._encryption_ready) {
            await self._encryption_ready;
          }
          if (
            extra_data &&
            self._encryption_enabled &&
            encoded_method._renc_pub
          ) {
            const plaintext = msgpack_packb(extra_data);
            const { nonce, ciphertext } = await encryptPayload(
              self._encryption_private_key,
              new Uint8Array(encoded_method._renc_pub),
              plaintext,
            );
            extra_data = {
              _enc: {
                v: 2,
                pub: self._encryption_public_key,
                nonce: nonce,
              },
              data: ciphertext,
            };
          }

          // The message consists of two segments, the main message and extra data
          let message_package = msgpack_packb(main_message);
          if (extra_data) {
            const extra = msgpack_packb(extra_data);
            const combined = new Uint8Array(
              message_package.length + extra.length,
            );
            combined.set(message_package);
            combined.set(extra, message_package.length);
            message_package = combined;
          }
          const total_size = message_package.length;
          if (
            total_size <= self._long_message_chunk_size + 1024 ||
            remote_method.__no_chunk__
          ) {
            self
              ._emit_message(message_package)
              .then(function () {
                if (timer) {
                  // Start the timer after message is sent successfully
                  timer.start();
                }
                if (!with_promise) {
                  // Fire-and-forget: resolve immediately after message is sent.
                  // Without this, the promise never resolves because no response
                  // is expected. This is critical for heartbeat callbacks which
                  // use _rpromise=false and are awaited in a loop.
                  resolve(null);
                }
              })
              .catch(function (err) {
                const error_msg = `Failed to send the request when calling method (${target_id}:${method_id}), error: ${err}`;
                if (reject) {
                  reject(new Error(error_msg));
                } else {
                  // No reject callback available, log the error to prevent unhandled promise rejections
                  console.warn("Unhandled RPC method call error:", error_msg);
                }
                if (timer && timer.started) {
                  timer.clear();
                }
              });
          } else {
            // send chunk by chunk
            self
              ._send_chunks(message_package, target_id, remote_parent)
              .then(function () {
                if (timer) {
                  // Start the timer after message is sent successfully
                  timer.start();
                }
                if (!with_promise) {
                  // Fire-and-forget: resolve immediately after message is sent
                  resolve(null);
                }
              })
              .catch(function (err) {
                const error_msg = `Failed to send the request when calling method (${target_id}:${method_id}), error: ${err}`;
                if (reject) {
                  reject(new Error(error_msg));
                } else {
                  // No reject callback available, log the error to prevent unhandled promise rejections
                  console.warn("Unhandled RPC method call error:", error_msg);
                }
                if (timer && timer.started) {
                  timer.clear();
                }
              });
          }
        } catch (err) {
          reject(err);
        }
      });
    }

    // Generate debugging information for the method
    remote_method.__rpc_object__ = encoded_method;
    const parts = method_id.split(".");

    remote_method.__name__ = encoded_method._rname || parts[parts.length - 1];
    if (remote_method.__name__.includes("#")) {
      remote_method.__name__ = remote_method.__name__.split("#")[1];
    }
    remote_method.__doc__ =
      encoded_method._rdoc || `Remote method: ${method_id}`;
    remote_method.__schema__ = encoded_method._rschema;
    // Prevent circular chunk sending
    remote_method.__no_chunk__ =
      encoded_method._rmethod === "services.built-in.message_cache.append";
    return remote_method;
  }

  get_client_info() {
    const services = [];
    for (let service of Object.values(this._services)) {
      services.push(this._extract_service_info(service));
    }

    return {
      id: this._client_id,
      services: services,
    };
  }

  async _handle_method(data) {
    let resolve = null;
    let reject = null;
    let heartbeat_task = null;
    try {
      assert(data.method && data.ctx && data.from);
      const method_name = data.from + ":" + data.method;
      const remote_workspace = data.from.split("/")[0];
      const remote_client_id = data.from.split("/")[1];
      // Make sure the target id is an absolute id
      data["to"] = data["to"].includes("/")
        ? data["to"]
        : remote_workspace + "/" + data["to"];
      data["ctx"]["to"] = data["to"];
      let local_workspace;
      if (!this._local_workspace) {
        local_workspace = data["to"].split("/")[0];
      } else {
        if (this._local_workspace && this._local_workspace !== "*") {
          assert(
            data["to"].split("/")[0] === this._local_workspace,
            "Workspace mismatch: " +
              data["to"].split("/")[0] +
              " != " +
              this._local_workspace,
          );
        }
        local_workspace = this._local_workspace;
      }
      const local_parent = data.parent;

      // E2E Decryption: if extra_data was encrypted, decrypt it now
      let callerEncryptionPub = null;
      if (data._enc && this._encryption_enabled) {
        const encInfo = data._enc;
        if (encInfo && typeof encInfo === "object" && encInfo.v === 2) {
          callerEncryptionPub = new Uint8Array(encInfo.pub);
          const nonce = new Uint8Array(encInfo.nonce);
          const ciphertext = new Uint8Array(data.data);
          let plaintext;
          try {
            plaintext = await decryptPayload(
              this._encryption_private_key,
              callerEncryptionPub,
              nonce,
              ciphertext,
            );
          } catch (e) {
            throw new Error(
              `Decryption failed for method ${method_name}. Invalid key or tampered data.`,
            );
          }
          // Unpack original extra_data and merge back
          const decoded = [];
          for (const item of decodeMulti(plaintext)) {
            decoded.push(item);
          }
          const originalExtra = decoded[0];
          delete data._enc;
          delete data.data;
          Object.assign(data, originalExtra);
          // Store caller's encryption pub in context
          data.ctx.encryption = true;
          data.ctx.caller_public_key = publicKeyToHex(callerEncryptionPub);
        }
      }

      if (data.promise) {
        // Decode the promise with the remote session id
        // Such that the session id will be passed to the remote as a parent session id
        const promise = await this._decode(
          data.promise === "*"
            ? this._expand_promise(data, callerEncryptionPub)
            : data.promise,
          data.session,
          local_parent,
          remote_workspace,
          local_workspace,
        );
        resolve = promise.resolve;
        reject = promise.reject;
        if (promise.heartbeat && promise.interval) {
          async function heartbeat() {
            try {
              // console.debug("Reset heartbeat timer: " + data.method);
              await promise.heartbeat();
            } catch (err) {
              console.error(err);
            }
          }
          heartbeat_task = setInterval(heartbeat, promise.interval * 1000);
          // Store the heartbeat task in the session store for cleanup
          if (data.session) {
            const session_store = this._get_session_store(data.session, false);
            if (session_store) {
              session_store.heartbeat_task = heartbeat_task;
            }
          }
        }
      }

      let method;

      try {
        method = indexObject(this._object_store, data["method"]);
        // Update last-activity time for session GC
        const methodParts = data["method"].split(".");
        if (methodParts.length > 1) {
          const topKey = methodParts[0];
          if (topKey !== "services" && topKey !== "message_cache") {
            // Skip system stores — they are not GC-managed sessions
            const topSession = this._object_store[topKey];
            if (topSession && typeof topSession === "object") {
              topSession._last_activity_at = Date.now();
            }
          }
        }
      } catch (e) {
        // Clean promise method detection - TYPE-BASED, not string-based
        if (this._is_promise_method_call(data["method"])) {
          // console.debug(
            // `Promise method ${data["method"]} not available (detected by session type), ignoring: ${method_name}`,
          // );
          return;
        }

        // Check if this is a session-based method call that might have expired
        const method_parts = data["method"].split(".");
        if (method_parts.length > 1) {
          const session_id = method_parts[0];
          // Check if the session exists but the specific method doesn't
          if (session_id in this._object_store) {
            // console.debug(
              // `Session ${session_id} exists but method ${data["method"]} not found, likely expired callback: ${method_name}`,
            // );
            // For expired callbacks, don't throw an exception, just log and return
            if (typeof reject === "function") {
              reject(new Error(`Method expired or not found: ${method_name}`));
            }
            return;
          } else {
            // console.debug(
              // `Session ${session_id} not found for method ${data["method"]}, likely cleaned up: ${method_name}`,
            // );
            // For cleaned up sessions, just log and return without throwing
            if (typeof reject === "function") {
              reject(new Error(`Session not found: ${method_name}`));
            }
            return;
          }
        }

        // console.debug(
          // `Failed to find method ${method_name} at ${this._client_id}`,
        // );
        const error = new Error(
          `Method not found: ${method_name} at ${this._client_id}`,
        );
        if (typeof reject === "function") {
          reject(error);
        } else {
          // Log the error instead of throwing to prevent unhandled exceptions
          console.warn(
            "Method not found and no reject callback:",
            error.message,
          );
        }
        return;
      }

      assert(
        method && typeof method === "function",
        "Invalid method: " + method_name,
      );

      // Check permission
      if (this._method_annotations.has(method)) {
        // For services, it should not be protected
        if (this._method_annotations.get(method).visibility === "protected") {
          // Allow access from same workspace
          if (local_workspace === remote_workspace) {
            // Access granted
          }
          // Check if remote workspace is in authorized_workspaces list
          else if (
            this._method_annotations.get(method).authorized_workspaces &&
            this._method_annotations
              .get(method)
              .authorized_workspaces.includes(remote_workspace)
          ) {
            // Access granted
          }
          // Allow manager access
          else if (
            remote_workspace === "*" &&
            remote_client_id === this._connection.manager_id
          ) {
            // Access granted
          }
          // Allow _rintf callbacks from the specific client they were sent to
          else if (
            this._method_annotations.get(method).rintf_allowed_caller
          ) {
            const allowed =
              this._method_annotations.get(method).rintf_allowed_caller;
            const caller = data.from || "";
            if (caller !== allowed) {
              throw new Error(
                "Permission denied for _rintf callback " +
                  method_name +
                  ", caller " +
                  caller +
                  " is not the allowed caller " +
                  allowed,
              );
            }
            // Access granted — caller matches the _rintf target
          } else {
            throw new Error(
              "Permission denied for invoking protected method " +
                method_name +
                ", workspace mismatch: " +
                local_workspace +
                " != " +
                remote_workspace,
            );
          }
        }
      } else {
        // For sessions, the target_id should match exactly
        let session_target_id =
          this._object_store[data.method.split(".")[0]].target_id;
        if (
          local_workspace === remote_workspace &&
          session_target_id &&
          session_target_id.indexOf("/") === -1
        ) {
          session_target_id = local_workspace + "/" + session_target_id;
        }
        if (session_target_id !== data.from) {
          throw new Error(
            "Access denied for method call (" +
              method_name +
              ") from " +
              data.from +
              " to target " +
              session_target_id,
          );
        }
      }

      // Check trusted_keys (encryption-based authentication)
      if (this._method_annotations.has(method)) {
        const annotation = this._method_annotations.get(method);
        if (annotation.trusted_keys) {
          if (!callerEncryptionPub) {
            throw new Error(
              `Encryption required for method ${method_name} (trusted_keys is set)`,
            );
          }
          const callerHex = publicKeyToHex(callerEncryptionPub);
          if (!annotation.trusted_keys.has(callerHex)) {
            throw new Error(
              `Caller's public key is not in the trusted keys list for ${method_name}`,
            );
          }
        }
      }

      // Make sure the parent session is still open
      // Skip for service methods — services are persistent and don't
      // depend on the originating session being alive.
      if (local_parent && !data.method.startsWith("services.")) {
        // The parent session should be a session that generate the current method call
        assert(
          this._get_session_store(local_parent, true) !== null,
          "Parent session was closed: " + local_parent,
        );
      }
      let args;
      if (data.args) {
        args = await this._decode(
          data.args,
          data.session,
          null,
          remote_workspace,
          null,
        );
      } else {
        args = [];
      }
      if (
        this._method_annotations.has(method) &&
        this._method_annotations.get(method).require_context
      ) {
        // if args.length + 1 is less than the required number of arguments we will pad with undefined
        // so we make sure the last argument is the context
        if (args.length + 1 < method.length) {
          for (let i = args.length; i < method.length - 1; i++) {
            args.push(undefined);
          }
        }
        args.push(data.ctx);
        // assert(
        //   args.length === method.length,
        //   `Runtime Error: Invalid number of arguments for method ${method_name}, expected ${method.length} but got ${args.length}`,
        // );
      }
      // console.debug(`Executing method: ${method_name} (${data.method})`);
      if (data.promise) {
        const result = method.apply(null, args);
        if (result instanceof Promise) {
          result
            .then((result) => {
              resolve(result);
            })
            .catch((err) => {
              reject(err);
            })
            .finally(() => {
              clearInterval(heartbeat_task);
            });
        } else {
          resolve(result);
          clearInterval(heartbeat_task);
        }
      } else {
        method.apply(null, args);
        clearInterval(heartbeat_task);
      }
    } catch (err) {
      if (reject) {
        reject(err);
      } else {
        console.error("Error during calling method: ", err);
      }
      clearInterval(heartbeat_task);
    }
  }

  encode(aObject, session_id) {
    return this._encode(aObject, session_id);
  }

  _get_session_store(session_id, create) {
    if (!session_id) {
      return null;
    }
    let store = this._object_store;
    const levels = session_id.split(".");
    if (create) {
      const last_index = levels.length - 1;
      for (let level of levels.slice(0, last_index)) {
        if (!store[level]) {
          // Instead of returning null, create intermediate sessions as needed
          store[level] = {};
        }
        store = store[level];
      }
      // Create the last level
      if (!store[levels[last_index]]) {
        store[levels[last_index]] = {};
        const now = Date.now();
        store[levels[last_index]]._created_at = now;
        store[levels[last_index]]._last_activity_at = now;
      }
      return store[levels[last_index]];
    } else {
      for (let level of levels) {
        if (!store[level]) {
          return null;
        }
        store = store[level];
      }
      return store;
    }
  }

  /**
   * Prepares the provided set of remote method arguments for
   * sending to the remote site, replaces all the callbacks with
   * identifiers
   *
   * @param {Array} args to wrap
   *
   * @returns {Array} wrapped arguments
   */
  async _encode(aObject, session_id, local_workspace) {
    const aType = typeof aObject;
    if (
      aType === "number" ||
      aType === "string" ||
      aType === "boolean" ||
      aObject === null ||
      aObject === undefined ||
      aObject instanceof Uint8Array
    ) {
      return aObject;
    }
    if (aObject instanceof ArrayBuffer) {
      return {
        _rtype: "memoryview",
        _rvalue: new Uint8Array(aObject),
      };
    }
    // Reuse the remote object
    if (aObject.__rpc_object__) {
      const _server = aObject.__rpc_object__._rserver || this._server_base_url;
      if (_server === this._server_base_url) {
        return aObject.__rpc_object__;
      } // else {
      //   console.debug(
      //     `Encoding remote function from a different server ${_server}, current server: ${this._server_base_url}`,
      //   );
      // }
    }

    let bObject;

    // skip if already encoded
    if (aObject.constructor instanceof Object && aObject._rtype) {
      // make sure the interface functions are encoded
      const temp = aObject._rtype;
      delete aObject._rtype;
      bObject = await this._encode(aObject, session_id, local_workspace);
      bObject._rtype = temp;
      return bObject;
    }

    if (
      isGenerator(aObject) ||
      isAsyncGenerator(aObject) ||
      isAsyncIterator(aObject) ||
      isSyncIterator(aObject)
    ) {
      // Handle generator/iterator objects
      assert(
        session_id && typeof session_id === "string",
        "Session ID is required for generator encoding",
      );
      const object_id = randId();
      const close_id = object_id + ":close";

      // Get the session store
      const store = this._get_session_store(session_id, true);
      assert(
        store !== null,
        `Failed to create session store ${session_id} due to invalid parent`,
      );

      // Check if it's an async generator/iterator
      const isAsync = isAsyncGenerator(aObject) || isAsyncIterator(aObject);

      // Define method to get next item from the generator/iterator
      const nextItemMethod = async () => {
        if (isAsync) {
          const result = await aObject.next();
          if (result.done) {
            delete store[object_id];
            delete store[close_id];
            return { _rtype: "stop_iteration" };
          }
          return result.value;
        } else {
          const result = aObject.next();
          if (result.done) {
            delete store[object_id];
            delete store[close_id];
            return { _rtype: "stop_iteration" };
          }
          return result.value;
        }
      };

      // Define method to close/cleanup the generator/iterator early
      const closeGeneratorMethod = async () => {
        try {
          if (typeof aObject.return === "function") {
            if (isAsync) {
              await aObject.return();
            } else {
              aObject.return();
            }
          }
        } catch (e) {
          // ignore close errors
        } finally {
          delete store[object_id];
          delete store[close_id];
        }
        return true;
      };

      // Store both methods in the session
      store[object_id] = nextItemMethod;
      store[close_id] = closeGeneratorMethod;

      // Create a method that will be used to fetch the next item from the generator
      bObject = {
        _rtype: "generator",
        _rserver: this._server_base_url,
        _rtarget: this._client_id,
        _rmethod: `${session_id}.${object_id}`,
        _rclose_method: `${session_id}.${close_id}`,
        _rpromise: "*",
        _rdoc: "Remote generator",
      };
      return bObject;
    } else if (typeof aObject === "function") {
      if (this._method_annotations.has(aObject)) {
        let annotation = this._method_annotations.get(aObject);
        bObject = {
          _rtype: "method",
          _rserver: this._server_base_url,
          _rtarget: this._client_id,
          _rmethod: annotation.method_id,
          _rpromise: "*",
          _rname: aObject.name,
        };
      } else {
        assert(typeof session_id === "string");
        let object_id;
        if (aObject.__name__) {
          object_id = `${randId()}#${aObject.__name__}`;
        } else {
          object_id = randId();
        }
        bObject = {
          _rtype: "method",
          _rserver: this._server_base_url,
          _rtarget: this._client_id,
          _rmethod: `${session_id}.${object_id}`,
          _rpromise: "*",
          _rname: aObject.name,
        };
        // Attach encryption public key for session methods (e.g. resolve/reject)
        if (this._encryption_enabled) {
          bObject._renc_pub = this._encryption_public_key;
        }
        let store = this._get_session_store(session_id, true);
        assert(
          store !== null,
          `Failed to create session store ${session_id} due to invalid parent`,
        );
        store[object_id] = aObject;
      }
      bObject._rdoc = aObject.__doc__;
      if (!bObject._rdoc) {
        try {
          const funcInfo = getFunctionInfo(aObject);
          if (funcInfo && !bObject._rdoc) {
            bObject._rdoc = `${funcInfo.doc}`;
          }
        } catch (e) {
          console.error("Failed to extract function docstring:", aObject);
        }
      }
      bObject._rschema = aObject.__schema__;
      return bObject;
    }
    const isarray = Array.isArray(aObject);

    for (let tp of Object.keys(this._codecs)) {
      const codec = this._codecs[tp];
      if (codec.encoder && aObject instanceof codec.type) {
        // TODO: what if multiple encoders found
        let encodedObj = await Promise.resolve(codec.encoder(aObject));
        if (encodedObj && !encodedObj._rtype) encodedObj._rtype = codec.name;
        // encode the functions in the interface object
        if (typeof encodedObj === "object") {
          const temp = encodedObj._rtype;
          delete encodedObj._rtype;
          encodedObj = await this._encode(
            encodedObj,
            session_id,
            local_workspace,
          );
          encodedObj._rtype = temp;
        }
        bObject = encodedObj;
        return bObject;
      }
    }

    if (
      /*global tf*/
      typeof tf !== "undefined" &&
      tf.Tensor &&
      aObject instanceof tf.Tensor
    ) {
      const v_buffer = aObject.dataSync();
      bObject = {
        _rtype: "ndarray",
        _rvalue: new Uint8Array(v_buffer.buffer),
        _rshape: aObject.shape,
        _rdtype: aObject.dtype,
      };
    } else if (
      /*global nj*/
      typeof nj !== "undefined" &&
      nj.NdArray &&
      aObject instanceof nj.NdArray
    ) {
      if (!aObject.selection || !aObject.selection.data) {
        throw new Error("Invalid NumJS array: missing selection or data");
      }
      const dtype = typedArrayToDtype(aObject.selection.data);
      bObject = {
        _rtype: "ndarray",
        _rvalue: new Uint8Array(aObject.selection.data.buffer),
        _rshape: aObject.shape,
        _rdtype: dtype,
      };
    } else if (aObject instanceof Error) {
      console.error(aObject);
      bObject = {
        _rtype: "error",
        _rvalue: aObject.toString(),
        _rtrace: aObject.stack,
      };
    }
    // send objects supported by structure clone algorithm
    // https://developer.mozilla.org/en-US/docs/Web/API/Web_Workers_API/Structured_clone_algorithm
    else if (
      aObject !== Object(aObject) ||
      aObject instanceof Boolean ||
      aObject instanceof String ||
      aObject instanceof Date ||
      aObject instanceof RegExp ||
      (typeof ImageData !== "undefined" && aObject instanceof ImageData) ||
      (typeof FileList !== "undefined" && aObject instanceof FileList) ||
      (typeof FileSystemDirectoryHandle !== "undefined" &&
        aObject instanceof FileSystemDirectoryHandle) ||
      (typeof FileSystemFileHandle !== "undefined" &&
        aObject instanceof FileSystemFileHandle) ||
      (typeof FileSystemHandle !== "undefined" &&
        aObject instanceof FileSystemHandle) ||
      (typeof FileSystemWritableFileStream !== "undefined" &&
        aObject instanceof FileSystemWritableFileStream)
    ) {
      bObject = aObject;
      // TODO: avoid object such as DynamicPlugin instance.
    } else if (aObject instanceof Blob) {
      let _current_pos = 0;
      async function read(length) {
        let blob;
        if (length) {
          blob = aObject.slice(_current_pos, _current_pos + length);
        } else {
          blob = aObject.slice(_current_pos);
        }
        const ret = new Uint8Array(await blob.arrayBuffer());
        _current_pos = _current_pos + ret.byteLength;
        return ret;
      }
      function seek(pos) {
        _current_pos = pos;
      }
      bObject = {
        _rtype: "iostream",
        _rnative: "js:blob",
        type: aObject.type,
        name: aObject.name,
        size: aObject.size,
        path: aObject._path || aObject.webkitRelativePath,
        read: await this._encode(read, session_id, local_workspace),
        seek: await this._encode(seek, session_id, local_workspace),
      };
    } else if (aObject instanceof ArrayBufferView) {
      const dtype = typedArrayToDtype(aObject);
      bObject = {
        _rtype: "typedarray",
        _rvalue: new Uint8Array(aObject.buffer),
        _rdtype: dtype,
      };
    } else if (aObject instanceof DataView) {
      bObject = {
        _rtype: "memoryview",
        _rvalue: new Uint8Array(aObject.buffer),
      };
    } else if (aObject instanceof Set) {
      bObject = {
        _rtype: "set",
        _rvalue: await this._encode(
          Array.from(aObject),
          session_id,
          local_workspace,
        ),
      };
    } else if (aObject instanceof Map) {
      bObject = {
        _rtype: "orderedmap",
        _rvalue: await this._encode(
          Array.from(aObject),
          session_id,
          local_workspace,
        ),
      };
    } else if (
      aObject.constructor === Object ||
      Array.isArray(aObject) ||
      aObject instanceof RemoteService
    ) {
      // Auto-register _rintf objects as local services
      if (
        !isarray &&
        aObject._rintf === true &&
        Object.keys(aObject).some(
          (k) => !k.startsWith("_") && typeof aObject[k] === "function",
        )
      ) {
        const serviceId = `_rintf_${randId()}`;
        // Resolve the allowed caller from the session's target_id.
        // Only the client this _rintf is being sent to can call it back.
        let allowedCaller = null;
        if (session_id) {
          const topKey = session_id.split(".")[0];
          const sessionStore = this._object_store[topKey];
          if (sessionStore && sessionStore.target_id) {
            allowedCaller = sessionStore.target_id;
          }
        }
        const serviceApi = { id: serviceId };
        if (allowedCaller) {
          serviceApi.config = {
            visibility: "protected",
            _rintf_allowed_caller: allowedCaller,
          };
        }

        // Add _dispose method for active lifecycle management.
        // The remote side (allowed caller) can call _dispose() to
        // actively unregister this _rintf service when it's done.
        const self = this;
        serviceApi._dispose = () => {
          self._unregisterRintfService(serviceId, allowedCaller);
        };

        for (const k of Object.keys(aObject)) {
          if (!k.startsWith("_") && typeof aObject[k] === "function") {
            serviceApi[k] = aObject[k];
          }
        }
        this.add_service(serviceApi, true);
        // Track in caller index for passive cleanup on disconnect
        if (allowedCaller) {
          if (!this._rintfCallerIndex[allowedCaller]) {
            this._rintfCallerIndex[allowedCaller] = new Set();
          }
          this._rintfCallerIndex[allowedCaller].add(serviceId);
        }
        // Store service_id back on the original object so the caller
        // can later call rpc.unregister_service(serviceId) to clean up.
        aObject._rintf_service_id = serviceId;
        // Encode all values — callables are now annotated as service methods
        bObject = {};
        for (const key of Object.keys(aObject)) {
          bObject[key] = await this._encode(
            aObject[key],
            session_id,
            local_workspace,
          );
        }
        // Encode _dispose so the remote side can call it
        bObject._dispose = await this._encode(
          serviceApi._dispose,
          session_id,
          local_workspace,
        );
        bObject._rintf_service_id = serviceId;
        return bObject;
      }

      // Fast path: if all values are primitives, return as-is
      if (isarray) {
        if (_allPrimitivesArray(aObject)) return aObject;
      } else if (
        !("_rtype" in aObject) &&
        !(aObject instanceof RemoteService)
      ) {
        if (_allPrimitivesObject(aObject)) return aObject;
      }
      bObject = isarray ? [] : {};
      const keys = Object.keys(aObject);
      for (let k of keys) {
        bObject[k] = await this._encode(
          aObject[k],
          session_id,
          local_workspace,
        );
      }
    } else {
      throw `hypha-rpc: Unsupported data type: ${aObject}, you can register a custom codec to encode/decode the object.`;
    }

    if (!bObject) {
      throw new Error("Failed to encode object");
    }
    return bObject;
  }

  async decode(aObject) {
    return await this._decode(aObject);
  }

  async _decode(
    aObject,
    remote_parent,
    local_parent,
    remote_workspace,
    local_workspace,
  ) {
    if (!aObject) {
      return aObject;
    }
    let bObject;
    if (aObject._rtype) {
      if (
        this._codecs[aObject._rtype] &&
        this._codecs[aObject._rtype].decoder
      ) {
        const temp = aObject._rtype;
        delete aObject._rtype;
        aObject = await this._decode(
          aObject,
          remote_parent,
          local_parent,
          remote_workspace,
          local_workspace,
        );
        aObject._rtype = temp;

        bObject = await Promise.resolve(
          this._codecs[aObject._rtype].decoder(aObject),
        );
      } else if (aObject._rtype === "method") {
        bObject = this._generate_remote_method(
          aObject,
          remote_parent,
          local_parent,
          remote_workspace,
          local_workspace,
        );
      } else if (aObject._rtype === "generator") {
        // Create a method to fetch next items from the remote generator
        const gen_method = this._generate_remote_method(
          aObject,
          remote_parent,
          local_parent,
          remote_workspace,
          local_workspace,
        );

        // Create close method if available
        let close_method = null;
        if (aObject._rclose_method) {
          const closeObj = {
            _rtype: "method",
            _rserver: aObject._rserver,
            _rtarget: aObject._rtarget,
            _rmethod: aObject._rclose_method,
            _rpromise: "*",
          };
          close_method = this._generate_remote_method(
            closeObj,
            remote_parent,
            local_parent,
            remote_workspace,
            local_workspace,
          );
        }

        // Create an async generator proxy with cleanup support
        async function* asyncGeneratorProxy() {
          let completedNormally = false;
          try {
            while (true) {
              const next_item = await gen_method();
              // Check for StopIteration signal
              if (next_item && next_item._rtype === "stop_iteration") {
                completedNormally = true;
                break;
              }
              yield next_item;
            }
          } catch (error) {
            console.error("Error in generator:", error);
            throw error;
          } finally {
            // If not completed normally, send close signal to clean up remote generator
            if (!completedNormally && close_method) {
              try {
                await close_method();
              } catch (e) {
                // ignore close errors
              }
            }
          }
        }
        bObject = asyncGeneratorProxy();
      } else if (aObject._rtype === "ndarray") {
        /*global nj tf*/
        //create build array/tensor if used in the plugin
        if (typeof nj !== "undefined" && nj.array) {
          if (Array.isArray(aObject._rvalue)) {
            aObject._rvalue = aObject._rvalue.reduce(_appendBuffer);
          }
          bObject = nj
            .array(new Uint8(aObject._rvalue), aObject._rdtype)
            .reshape(aObject._rshape);
        } else if (typeof tf !== "undefined" && tf.Tensor) {
          if (Array.isArray(aObject._rvalue)) {
            aObject._rvalue = aObject._rvalue.reduce(_appendBuffer);
          }
          const arraytype = dtypeToTypedArray[aObject._rdtype];
          bObject = tf.tensor(
            new arraytype(aObject._rvalue),
            aObject._rshape,
            aObject._rdtype,
          );
        } else {
          //keep it as regular if transfered to the main app
          bObject = aObject;
        }
      } else if (aObject._rtype === "error") {
        bObject = new Error(
          "RemoteError: " + aObject._rvalue + "\n" + (aObject._rtrace || ""),
        );
      } else if (aObject._rtype === "typedarray") {
        const arraytype = dtypeToTypedArray[aObject._rdtype];
        if (!arraytype)
          throw new Error("unsupported dtype: " + aObject._rdtype);
        const buffer = aObject._rvalue.buffer.slice(
          aObject._rvalue.byteOffset,
          aObject._rvalue.byteOffset + aObject._rvalue.byteLength,
        );
        bObject = new arraytype(buffer);
      } else if (aObject._rtype === "memoryview") {
        bObject = aObject._rvalue.buffer.slice(
          aObject._rvalue.byteOffset,
          aObject._rvalue.byteOffset + aObject._rvalue.byteLength,
        ); // ArrayBuffer
      } else if (aObject._rtype === "iostream") {
        if (aObject._rnative === "js:blob") {
          const read = await this._generate_remote_method(
            aObject.read,
            remote_parent,
            local_parent,
            remote_workspace,
            local_workspace,
          );
          const bytes = await read();
          bObject = new Blob([bytes], {
            type: aObject.type,
            name: aObject.name,
          });
        } else {
          bObject = {};
          for (let k of Object.keys(aObject)) {
            if (!k.startsWith("_")) {
              bObject[k] = await this._decode(
                aObject[k],
                remote_parent,
                local_parent,
                remote_workspace,
                local_workspace,
              );
            }
          }
        }
        bObject["__rpc_object__"] = aObject;
      } else if (aObject._rtype === "orderedmap") {
        bObject = new Map(
          await this._decode(
            aObject._rvalue,
            remote_parent,
            local_parent,
            remote_workspace,
            local_workspace,
          ),
        );
      } else if (aObject._rtype === "set") {
        bObject = new Set(
          await this._decode(
            aObject._rvalue,
            remote_parent,
            local_parent,
            remote_workspace,
            local_workspace,
          ),
        );
      } else {
        const temp = aObject._rtype;
        delete aObject._rtype;
        bObject = await this._decode(
          aObject,
          remote_parent,
          local_parent,
          remote_workspace,
          local_workspace,
        );
        bObject._rtype = temp;
      }
    } else if (aObject.constructor === Object || Array.isArray(aObject)) {
      const isarray = Array.isArray(aObject);
      // Fast path: skip recursive descent if all values are primitives
      if (isarray) {
        if (_allPrimitivesArray(aObject)) return aObject;
      } else {
        if (_allPrimitivesObject(aObject)) return aObject;
      }
      bObject = isarray ? [] : {};
      for (let k of Object.keys(aObject)) {
        if (isarray || aObject.hasOwnProperty(k)) {
          const v = aObject[k];
          bObject[k] = await this._decode(
            v,
            remote_parent,
            local_parent,
            remote_workspace,
            local_workspace,
          );
        }
      }
    } else {
      bObject = aObject;
    }
    if (bObject === undefined) {
      throw new Error("Failed to decode object");
    }
    return bObject;
  }

  _expand_promise(data, callerEncryptionPub) {
    const target = data.from.split("/")[1];
    const session = data.session;
    const method = data.method;
    const heartbeat = {
      _rtype: "method",
      _rtarget: target,
      _rmethod: `${session}.heartbeat`,
      _rdoc: `heartbeat callback for method: ${method}`,
    };
    const resolve = {
      _rtype: "method",
      _rtarget: target,
      _rmethod: `${session}.resolve`,
      _rdoc: `resolve callback for method: ${method}`,
    };
    const reject = {
      _rtype: "method",
      _rtarget: target,
      _rmethod: `${session}.reject`,
      _rdoc: `reject callback for method: ${method}`,
    };
    // Attach caller's encryption pubkey so responses are encrypted back
    if (callerEncryptionPub) {
      heartbeat._renc_pub = callerEncryptionPub;
      resolve._renc_pub = callerEncryptionPub;
      reject._renc_pub = callerEncryptionPub;
    }
    return {
      heartbeat,
      resolve,
      reject,
      interval: data.t,
    };
  }
}
